import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, Menu, Pagination } from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { exportPurchasesToPDF } from "../../utils/exportPurchases";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

function Row({ row }) {
  const [open, setOpen] = React.useState(false);
  const itemCount = row.purchase_items?.length ?? 0;
  const { t } = useTranslation();

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.supplier_details?.name || "-"}</TableCell>
        <TableCell>{row.purchase_date || "-"}</TableCell>
        <TableCell>
          {row.discount ?? "-"} {row.currency_details?.title ?? ""}
        </TableCell>
        <TableCell>
          {firstCurrencyValue(row.total_price)} {row.currency_details?.title ?? ""}
        </TableCell>
        <TableCell>
          {firstCurrencyValue(row.total_paid)} {row.currency_details?.title ?? ""}
        </TableCell>
        <TableCell>
          {firstCurrencyValue(row.total_remained_amount)}{" "}
          {row.currency_details?.title ?? ""}
        </TableCell>
        <TableCell>{itemCount}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t("ReportIndex.collapse_title")}
              </Typography>

              <Table size="small" aria-label="purchased-items">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("ReportIndex.collapse_product")}</TableCell>
                    <TableCell>{t("ReportIndex.collapse_qty")}</TableCell>
                    <TableCell>{t("ReportIndex.collapse_buyPrice")}</TableCell>
                    <TableCell>{t("ReportIndex.collapse_salePrice")}</TableCell>
                    <TableCell>{t("ReportIndex.collapse_unit")}</TableCell>
                    <TableCell>
                      {t("ReportIndex.collapse_expireDate")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.purchase_items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell component="th" scope="row">
                        {item.product_details?.name}
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.buy_price}</TableCell>
                      <TableCell>{item.sale_price}</TableCell>
                      <TableCell>{item.unit_details?.title}</TableCell>
                      <TableCell>{item.expire_date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    supplier_details: PropTypes.shape({ name: PropTypes.string }),
    purchase_date: PropTypes.string,
    discount: PropTypes.string,
    total_price: PropTypes.any,
    total_paid: PropTypes.any,
    total_remained_amount: PropTypes.any,
    purchase_items: PropTypes.array,
  }).isRequired,
};

export default function PurchaseTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const queryString = useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedSupplier) params.push(`supplier=${selectedSupplier}`);
    params.push(`page=${page}`);
    if (rowsPerPage !== -1) {
      params.push(`page_size=${rowsPerPage}`);
    }
    return params.join("&");
  }, [startDate, endDate, selectedSupplier, page, rowsPerPage]);

  const {
    data: purchasesData,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    [
      "purchase",
      startDate?.format("YYYY-MM-DD"),
      endDate?.format("YYYY-MM-DD"),
      selectedSupplier,
      page,
      rowsPerPage,
    ],
    `shop/purchases/?${queryString}`,
    token
  );

  // Fetch suppliers for filter dropdown
  const { data: suppliersData } = useFetchObjects(
    ["suppliers"],
    "shop/suppliers",
    token
  );

  const suppliers = suppliersData?.results || [];

  useEffect(() => {
    refetch();
  }, [startDate, endDate, selectedSupplier, page, rowsPerPage, refetch]);

  const purchases = purchasesData && purchasesData.results ? purchasesData.results : Array.isArray(purchasesData) ? purchasesData : [];
  const totalCount = purchasesData && purchasesData.count ? purchasesData.count : purchases.length;

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    return purchases.reduce((acc, purchase) => {
      const totalAmount = parseFloat(firstCurrencyValue(purchase.total_price)) || 0;
      const totalPaid = parseFloat(firstCurrencyValue(purchase.total_paid)) || 0;
      const totalRemained = parseFloat(firstCurrencyValue(purchase.total_remained_amount)) || 0;
      
      return {
        totalAmount: acc.totalAmount + totalAmount,
        totalPaid: acc.totalPaid + totalPaid,
        totalRemained: acc.totalRemained + totalRemained
      };
    }, { totalAmount: 0, totalPaid: 0, totalRemained: 0 });
  }, [purchases]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `purchases_${today}`;

    if (type === "excel") {
      handleClose();
    } else if (type === "pdf") {
      exportPurchasesToPDF(purchases, `${base}.pdf`);
    }

    handleClose();
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <InputLabel>{t("ReportIndex.select_supplier")}</InputLabel>
                  <Select
                    value={selectedSupplier}
                    onChange={(e) => setSelectedSupplier(e.target.value)}
                    label={t("ReportIndex.select_supplier")}
                  >
                    <MenuItem value="">{t("ReportIndex.all_suppliers")}</MenuItem>
                    {(suppliers || []).map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedSupplier("");
                    setPage(1);
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label="purchase table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: "center" }}>
                <Typography variant="h4">
                  {t("ReportIndex.tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("ReportIndex.table_supplier")}</TableCell>
              <TableCell>{t("ReportIndex.table_date")}</TableCell>
              <TableCell>{t("ReportIndex.table_discount")}</TableCell>
              <TableCell>{t("Total Amount")}</TableCell>
              <TableCell>{t("ReportIndex.table_totalPaid")}</TableCell>
              <TableCell>{t("ReportIndex.table_totalRemained")}</TableCell>
              <TableCell>{t("ReportIndex.table_itemCount")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchases?.map((purchase) => (
              <Row key={purchase.id} row={purchase} />
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalPaid.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRemained.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
    </>
  );
}
